/**
 * ── THE ONE ANGLES ⇄ VECTOR CONVERTER, IN A MODULE THAT IMPORTS NOTHING (B2) ────────────────────
 *
 * These two functions lived in `sky.ts` since E2 and are re-exported from there unchanged. They
 * moved here because B2 gave the editor a PURE placement module (`editor/placement.ts`, held in
 * `tests/purity.test.ts`'s `PURE_FILES`) that has to turn a surface normal into a spot light's
 * `azimuthDeg`/`elevationDeg` — and `sky.ts` value-imports `three/webgpu`. B1 met exactly this
 * shape with `outline.ts`, which had to stay type-only on `select.ts` for the same reason: a pure
 * file whose runtime chain reaches `three` is pure only on paper, and the arithmetic it exists to
 * make checkable in `npm run check` would sit behind the renderer's module graph.
 *
 * The alternative — a second copy of the trigonometry inside `src/editor` — is the failure the
 * angle vocabulary was invented to prevent (see `sunPositionFromAngles` below: a directional light's
 * pose was once a raw vector inherited by a level rotated 90° underneath it). One converter, one
 * inverse, one module; every consumer (the sun, the sky dome, an authored light's aim line, the
 * shadow camera, and now a dropped spot's beam) reads the same two functions.
 *
 * ⚠️ IT MUST STAY IMPORT-FREE. `world/schema.ts` documents this vocabulary and
 * `tools/bundler/bake-world.ts` runs `world/schema.ts` unbundled under
 * `node --experimental-strip-types`; nothing on that chain may reach `three`.
 */

/**
 * World position of a directional light from compass angles, with the light TARGETING THE ORIGIN.
 *
 * `azimuthDeg` is a compass bearing in the XZ plane: **0 = +Z, 90 = +X** (clockwise seen from
 * above, i.e. `atan2(x, z)`). `elevationDeg` is the angle above the horizon; 90 is straight up.
 * `distanceM` does not change the light's DIRECTION at all — a directional light has none — but it
 * does place the shadow camera, which is why it is data and not a constant.
 *
 * Pure and unit-tested, and the reason the world schema stores angles rather than a vector: a sun
 * is an art decision expressed as "where is it in the sky", and a raw `[12, 18, 8]` is the shape of
 * the bug this whole exercise found (a vector posed for the gym, inherited by a level rotated 90°
 * underneath it, with nothing anywhere to say so).
 */
export function sunPositionFromAngles(
  azimuthDeg: number,
  elevationDeg: number,
  distanceM: number,
): [number, number, number] {
  const az = (azimuthDeg * Math.PI) / 180;
  const el = (elevationDeg * Math.PI) / 180;
  const horizontal = distanceM * Math.cos(el);
  return [horizontal * Math.sin(az), distanceM * Math.sin(el), horizontal * Math.cos(az)];
}

/**
 * The EXACT INVERSE of `sunPositionFromAngles` — a world position back to compass angles (E2).
 *
 * It lives beside its inverse for the reason stated above it: `sunPositionFromAngles` is "the one
 * converter", and the moment the editor could DRAG a directional light's gizmo there had to be a
 * way back from a position to `azimuthDeg`/`elevationDeg`/`distanceM`. A second copy of that
 * trigonometry in `src/editor` would be a second answer to "which way is the light", which is
 * exactly the failure the angle vocabulary exists to prevent.
 *
 * A zero-length position has no direction; it returns the schema's own north-at-45° default rather
 * than NaN (rule 6: degrade, never throw).
 */
export function anglesFromSunPosition(
  x: number,
  y: number,
  z: number,
): { azimuthDeg: number; elevationDeg: number; distanceM: number } {
  const distanceM = Math.hypot(x, y, z);
  if (distanceM < 1e-6) return { azimuthDeg: 0, elevationDeg: 45, distanceM: 1 };
  return {
    azimuthDeg: (Math.atan2(x, z) * 180) / Math.PI,
    elevationDeg: (Math.asin(Math.max(-1, Math.min(1, y / distanceM))) * 180) / Math.PI,
    distanceM,
  };
}
